import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, UserPlus, UserCog, ShieldCheck, ShieldOff, KeyRound } from "lucide-react";

const ACTION_META = {
  user_created: { label: "Account created", Icon: UserPlus, tone: "olive" },
  user_updated: { label: "Account updated", Icon: UserCog, tone: "sage" },
  "2fa_enabled": { label: "2FA enabled", Icon: ShieldCheck, tone: "olive" },
  "2fa_disabled": { label: "2FA disabled", Icon: ShieldOff, tone: "terracotta" },
  recovery_codes_regenerated: { label: "Recovery codes regenerated", Icon: KeyRound, tone: "sage" },
};

export default function AccessTrail() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/access-log")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="space-y-6" data-testid="access-trail-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Access Trail</h1>
          <p className="text-[color:var(--ink-2)] text-sm">Who provisioned whom, role changes, and 2FA events — fully traceable.</p>
        </div>
        <button className="btn btn-ghost h-11" onClick={load} data-testid="access-refresh"><RefreshCw size={16} /> Refresh</button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[color:var(--muted)] border-b border-[color:var(--line)]">
                <th className="p-3 font-medium whitespace-nowrap">When</th>
                <th className="p-3 font-medium">Actor</th>
                <th className="p-3 font-medium">Action</th>
                <th className="p-3 font-medium">Target</th>
                <th className="p-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody data-testid="access-rows">
              {rows.map((r) => {
                const m = ACTION_META[r.action] || { label: r.action, Icon: UserCog, tone: "sage" };
                const Icon = m.Icon;
                return (
                  <tr key={r.id} className="border-b border-[color:var(--line)] align-top">
                    <td className="p-3 whitespace-nowrap tabnum text-xs">{fmt(r.timestamp)}</td>
                    <td className="p-3">{r.actor_email || "—"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--${m.tone}-light)] text-[color:var(--${m.tone})]`}>
                        <Icon size={13} /> {m.label}
                      </span>
                    </td>
                    <td className="p-3">
                      {r.target_name ? (
                        <div>
                          <div className="font-medium">{r.target_name}</div>
                          <div className="text-xs text-[color:var(--muted)]">{r.target_login} · {r.target_role}</div>
                        </div>
                      ) : (r.target_login || "—")}
                    </td>
                    <td className="p-3 text-xs text-[color:var(--ink-2)] max-w-xs break-words">
                      {r.details && Object.keys(r.details).length ? JSON.stringify(r.details) : "—"}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-[color:var(--muted)]" data-testid="access-empty">No access events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
